import {
  MutationCache,
  Query,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query'
import { useAuthStore } from '~/@api/client/authStore'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { commonErrorHandler, type ApiError } from './commonErrorHandler'

// Singleton QueryClient instance - vytvoríme ho len raz
let queryClientInstance: QueryClient | null = null

function useQueryClient() {
  const { removeAuthToken } = useAuthStore()
  const localNotifications = useLocalNotifications()

  // Vytvoríme QueryClient len raz (singleton pattern)
  if (!queryClientInstance) {
    queryClientInstance = createQueryClient(
      (errorMessage) =>
        localNotifications.setNotification('error', errorMessage),
      () => {
        removeAuthToken()
        window.location.reload()
      }
    )
  }

  return queryClientInstance
}

export { useQueryClient }

function createQueryClient(
  onDisplayErrorNotification: (errorMessage: string) => void,
  onUnauthorizedError: () => void
): QueryClient {
  function handleQueryError(
    error: Error,
    query: Query<unknown, unknown, unknown>
  ) {
    const meta = query.meta as { logoutOnFailedToFetch?: boolean } | undefined
    commonErrorHandler(
      error as ApiError,
      onDisplayErrorNotification,
      onUnauthorizedError,
      meta?.logoutOnFailedToFetch === true
    )
  }

  function handleMutationError(error: unknown) {
    commonErrorHandler(
      error as ApiError,
      onDisplayErrorNotification,
      onUnauthorizedError
    )
  }

  return new QueryClient({
    queryCache: new QueryCache({
      onError: handleQueryError,
    }),
    mutationCache: new MutationCache({
      onError: handleMutationError,
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 1, // 1 minute
        retry: false,
      },
    },
  })
}
